import styled from 'styled-components'
import { Body, Headline } from '@shipt/design-system-typography'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { screenSizes } from '@shipt/design-system-themes'
import { objectStorageUrl } from '@/features/shared/utils/objectStorageUrl'
import { Box, Column, Row, Stack } from '@shipt/design-system-layouts'
import { type PlaceholderData } from '@/features/cms/components/CMS/types'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { theme } from '@/features/shared/constants/shiptTheme'

export const UngatedHomepageValueProposition = ({
  content_type_id,
  id,
}: Pick<PlaceholderData, 'content_type_id' | 'id'>) => {
  const { trackCMSElementViewed } = useCMSTrackingEvents()
  const heading = 'Shoppers with Shipt deliver quality'
  return (
    <SectionContainer {...getCMSWrapperProps({ content_type_id, id })} as="section">
      <ScrollIntoView
        onInView={() => {
          trackCMSElementViewed({
            content: 'Value Proposition',
          })
        }}
      >
        <Stack
          padding={{ base: ['xxl', 0], md: ['5xl', 0] }}
          justify="center"
          align={{ md: 'start' }}
          direction={{ base: 'column', sm: 'row' }}
          spacing={{ base: 'lg', sm: 'xl' }}
        >
          <Box flexBasis="0%" flexGrow={4} className="contained">
            <Stack direction={{ base: 'column-reverse', md: 'column' }} spacing="xxl">
              <Image
                src={objectStorageUrl('shipt-party-supplies.png')}
                height={421}
                width={421}
                alt={heading}
                style={{ objectFit: 'contain', borderRadius: 12 }}
              />
              <Column spacing="lg">
                <Headline size="xxl" weight="medium" as="h2">
                  {heading}
                </Headline>
                <Body size="xl" weight="medium" variant="secondary">
                  Your orders are in dependable, detail-oriented hands.
                </Body>
              </Column>
            </Stack>
          </Box>
          <Row flexBasis="0%" flexGrow={5} justify="center" display={{ base: 'none', md: 'flex' }}>
            <Image
              src={objectStorageUrl('shopper-progress.png')}
              alt="Order again with Shipt"
              height={617}
              width={385}
            />
          </Row>
          <Box flexBasis="0%" flexGrow={3}>
            <Stack
              direction={{ base: 'column', sm: 'column-reverse', md: 'column' }}
              spacing={{ base: 'lg', md: 'xxl' }}
            >
              <div>
                <Body size={{ base: 'xl', lg: 'xxl' }} weight="medium">
                  “My usual shopper is so fantastic with efficiency & communication — he sets my
                  mind at ease so much, because I know that he will always pick the best choices.”
                </Body>
                <Box marginTop="xl">
                  <Body size="xxl" weight="medium">
                    Julie S.
                  </Body>
                </Box>
              </div>
              <Box display={{ base: 'none', md: 'block' }}>
                <Image
                  src={objectStorageUrl('homepage_phonechat.webp')}
                  alt="Fantastic shopper communication"
                  height={358}
                  width={315}
                />
              </Box>
              <Box display={{ base: 'block', md: 'none' }}>
                <Image
                  src={objectStorageUrl('homepage_shopper_phonechat-refresh.png')}
                  height={279}
                  width={358}
                  alt="Fantastic shopper and member relationship"
                />
              </Box>
            </Stack>
          </Box>
        </Stack>
      </ScrollIntoView>
    </SectionContainer>
  )
}

const SectionContainer = styled(GridLayoutContainer)`
  --content-max-width: 1280px;
  width: 100%;
  background-color: ${theme.color.background.base.neutral.subtle_100};

  img {
    display: block;
    height: auto;
  }

  @media ${screenSizes.max_mobile} {
    img {
      width: 100%;
    }

    .contained {
      max-width: 50%;
    }
  }

  @media ${screenSizes.max_smMobile} {
    .contained {
      max-width: 100%;
    }
  }
`
